import { getTransitionName, longDateFormatter, promiseAllKeyed } from "@schema-benchmarks/utils";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import type { MDXModule } from "mdx/types";

import { AvatarList } from "#src/shared/components/avatar";
import { generateMetadata } from "#src/shared/data/meta";
import { useDateFormatter } from "#src/shared/hooks/format/use-date-formatter";
import { preloadImages } from "#src/shared/lib/fetch";

import { getAvatarUrl, getBlog } from "./-query";

const importMdx = (filePath: string) =>
  queryOptions({
    queryKey: ["mdx", filePath],
    queryFn: (): Promise<MDXModule> => import(`./-content/${filePath.replace(/\.mdx$/, "")}.mdx`),
  });

export const Route = createFileRoute("/blog/$slug")({
  component: RouteComponent,
  async loader({ context: { queryClient }, params: { slug }, abortController }) {
    const data = await queryClient.ensureQueryData(getBlog(slug, abortController.signal));
    const images = data.authors.map(getAvatarUrl);
    if (typeof data.cover !== "string") images.push(data.cover.src);
    const { mdxModule } = await promiseAllKeyed({
      mdxModule: queryClient.ensureQueryData(importMdx(data._meta.filePath)),
      images: preloadImages(images),
    });
    if (typeof mdxModule.prefetch === "function") await mdxModule.prefetch({ queryClient });
    return { crumb: data.title, ...data };
  },
  head: ({ loaderData, params }) =>
    loaderData
      ? generateMetadata({
          title: loaderData.title,
          description: loaderData.description,
          openGraph: {
            url: `/blog/${params.slug}`,
          },
        })
      : {},
  staticData: { crumb: undefined },
});

function RouteComponent() {
  const { slug } = Route.useParams();
  const { data } = useSuspenseQuery(getBlog(slug));
  const {
    data: { default: MDXContent },
  } = useSuspenseQuery(importMdx(data._meta.filePath));
  const getTransitionStyle = (element: string) => ({
    style: {
      viewTransitionName: `${getTransitionName("blog-header", { slug })}-${element}`,
    },
  });
  const formatDate = useDateFormatter(longDateFormatter);
  return (
    <>
      <h1 className="blog-title typo-headline2" {...getTransitionStyle("title")}>
        {data.title}
      </h1>
      <div className="blog-dateline typo-subtitle2">
        <AvatarList
          {...getTransitionStyle("author")}
          items={data.authors.map((author) => ({
            label: author,
            src: getAvatarUrl(author),
          }))}
          size="lg"
        />
        <time
          dateTime={data.published.toISOString().split("T")[0]}
          suppressHydrationWarning
          {...getTransitionStyle("date")}
          aria-label={longDateFormatter.format(data.published)}
        >
          {formatDate(data.published)}
        </time>
      </div>
      {/* oxlint-disable-next-line jsx-a11y/prefer-tag-over-role */}
      <div role="img" className="blog-cover" {...getTransitionStyle("cover")}>
        {typeof data.cover === "string" ? (
          <p className="typo-headline2" {...getTransitionStyle("cover-text")}>
            {data.cover}
          </p>
        ) : (
          <picture>
            {data.cover.src_light && (
              <source media="(prefers-color-scheme: light)" srcSet={data.cover.src_light} />
            )}
            {data.cover.src_dark && (
              <source media="(prefers-color-scheme: dark)" srcSet={data.cover.src_dark} />
            )}
            <img
              src={data.cover.src}
              alt={data.cover.alt}
              style={{ objectFit: data.cover.fit ?? "cover" }}
            />
          </picture>
        )}
      </div>
      <MDXContent components={{ wrapper: "div" }} />
    </>
  );
}
